// @file Pinia store for managing emojis data
import {
  categoryTitles,
  emojiCodeToUnicode,
  getEmojiKey,
  isEmojiText,
  PLAIN_HEART_EMOJI_STRING,
  RED_HEART_EMOJI_STRING,
  segmentText,
} from '@@/bits/emoji'
import { captureException } from '@@/bits/error_tracker'
import { stripHtml } from '@@/bits/html_parser'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { tryCatch } from '@@/bits/try_catch'
import type { EmojiCategory, EmojiCollectionsBundle, Emoji as EmojiType } from '@padlet/arvo'
import { EmojiVersion, fetchEmojis } from '@padlet/arvo'
import { memoize } from 'es-toolkit'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

/**
 * Takes an emoji category and ensures it has a title by using the default title from categoryTitles if needed
 * @param category The emoji category object that may be missing a title
 * @returns A complete emoji category object with all required fields including title
 */
const getFullCategoryObject = (category: EmojiCategory): Required<EmojiCategory> => {
  return {
    ...category,
    title: category.title ?? categoryTitles[category.label as keyof typeof categoryTitles],
  }
}

// Wrapper to support memoization with multiple arguments
const fetchEmojisWrapper = async ({
  version,
  options,
}: {
  version: EmojiVersion
  options?: Parameters<typeof fetchEmojis>[1]
}): ReturnType<typeof fetchEmojis> => {
  return await fetchEmojis(version, options)
}

const memoizedFetchEmojis = memoize(fetchEmojisWrapper, {
  getCacheKey: (args) => asciiSafeStringify(args),
})

const useEmojisStore = defineStore('emojis', () => {
  const isLoading = ref(true)
  const emojiData = shallowRef<EmojiCollectionsBundle>({})

  const emojiDataAsFlatArray = computed(() => {
    if (!(Object.keys(emojiData.value).length > 0)) return []
    return Object.values(emojiData.value).reduce<EmojiType[]>((accumulatedEmojis, currentEmojiCollection) => {
      return [
        ...accumulatedEmojis,
        ...currentEmojiCollection.emojis.map((emoji) => ({
          ...emoji,
          category: getFullCategoryObject(currentEmojiCollection.category),
        })),
      ]
    }, [])
  })

  /**
   * Maps Unicode character strings to their full emoji objects
   *
   * Example structure:
   * Map {
   *   "😀" => {
   *     code: "1f600",
   *     name: "grinning face",
   *     shortname: ":grinning:",
   *     url: "https://padlet.net/icons/v8.0/svg/1f600.svg",
   *     category: { label: "smileys_emotion", title: "Smileys & Emotion" }
   *   },
   *   "❤️" => {
   *     code: "2764-fe0f",
   *     name: "red heart",
   *     shortname: ":heart:",
   *     url: "https://padlet.net/icons/v8.0/svg/2764-fe0f.svg",
   *     category: { label: "symbols", title: "Symbols" }
   *   }, ...
   * }
   */
  const emojiUnicodeMap = computed(() => {
    const map = new Map<string, EmojiType>()
    emojiDataAsFlatArray.value.forEach((emoji) => {
      const unicodeCharacter = emojiCodeToUnicode(emoji.code)
      if (!isEmpty(unicodeCharacter)) {
        map.set(unicodeCharacter, emoji)
      }
    })
    return map
  })

  /**
   * Maps `<category>:<URL>` to their full emoji objects.
   *
   * Example structure:
   * Map {
   *   "people:https://padlet.net/icons/v8.0/svg/1f600.svg" => {
   *     code: "1f600",
   *     name: "grinning face",
   *     shortname: ":grinning:",
   *     url: "https://padlet.net/icons/v8.0/svg/1f600.svg",
   *     category: { label: "people", title: "Smileys & Emotion" }
   *   },
   *   "symbols:https://padlet.net/icons/v8.0/svg/2764-fe0f.svg" => {
   *     code: "2764-fe0f",
   *     name: "red heart",
   *     shortname: ":heart:",
   *     url: "https://padlet.net/icons/v8.0/svg/2764-fe0f.svg",
   *     category: { label: "symbols", title: "Symbols" }
   *   }, ...
   * }
   */
  const emojiCategorizedUrlMap = computed(() => {
    const map = new Map<string, EmojiType>()
    emojiDataAsFlatArray.value.forEach((emoji) => {
      map.set(getEmojiKey(emoji), emoji)
    })
    return map
  })

  /**
   * Maps icon code strings to their full emoji objects
   *
   * Example structure:
   * Map {
   *   "1f600" => {
   *     code: "1f600",
   *     name: "grinning face",
   *     shortname: ":grinning:",
   *     url: "https://padlet.net/icons/v8.0/svg/1f600.svg",
   *     category: { label: "smileys_emotion", title: "Smileys & Emotion" }
   *   },
   *   "2764-fe0f" => {
   *     code: "2764-fe0f",
   *     name: "red heart",
   *     shortname: ":heart:",
   *     url: "https://padlet.net/icons/v8.0/svg/2764-fe0f.svg",
   *     category: { label: "symbols", title: "Symbols" }
   *   }, ...
   * }
   */
  const emojiCodeMap = computed(() => {
    const map = new Map<string, EmojiType>()
    emojiDataAsFlatArray.value.forEach((emoji) => {
      map.set(emoji.code, emoji)
    })
    return map
  })

  /**
   * Fetches JoyPixels emoji data from the server.
   * We only fetch SVG format emojis to:
   * 1. Reduce the amount of caching required (vs caching both PNG and SVG)
   * 2. Prevent duplicate emojis in recents that only differ by file extension
   * @returns Promise that resolves when emoji data is loaded
   */
  async function fetchEmojiData(): Promise<void> {
    // if already fetched, return
    if (emojiUnicodeMap.value.size > 0) return

    isLoading.value = true

    const [emojis, error] = await tryCatch(
      async () => await memoizedFetchEmojis({ version: EmojiVersion.v8p0, options: { format: 'svg' } }),
    )

    if (error) {
      captureException(error, { context: 'emojis_store' })
      isLoading.value = false
      return
    }

    emojiData.value = emojis
    isLoading.value = false
  }

  /**
   * Checks if a given text string represents a valid emoji character
   * @param strippedText The text to check, should be stripped of HTML
   * @returns True if the text is a valid emoji character, false otherwise
   */
  function isEmojiCharacter(strippedText: string): boolean {
    // Check for direct match first
    if (emojiUnicodeMap.value.has(strippedText)) {
      return true
    }

    if (strippedText === PLAIN_HEART_EMOJI_STRING || strippedText === RED_HEART_EMOJI_STRING) {
      return true
    }

    // For complex emojis that might not be in our database, use a more comprehensive check
    // This handles compound emojis, ZWJ sequences, and other complex emoji patterns
    if (isEmojiText(strippedText)) {
      return true
    }

    return false
  }

  /**
   * Gets emoji data for a text string containing 1-3 emojis.
   * Only returns data if text contains exactly 1-3 emojis, all characters are valid emojis, and all emojis exist in our emoji database.
   * @param text The text to get emoji data for, can contain HTML
   * @returns Array of emoji objects if text contains only 1-3 emojis, empty array otherwise
   */
  function getEmojisData(text: string): EmojiType[] {
    const strippedText = stripHtml(text).trim()
    const textSegments = segmentText(strippedText)

    const nonEmojiSegments = textSegments.filter((segment) => {
      return !/^\s*$/.test(segment) && !isEmojiCharacter(segment)
    })
    if (nonEmojiSegments.length > 0) return []

    const emojiObjects: EmojiType[] = []
    for (const emojiSegment of textSegments) {
      let emojiObject = emojiUnicodeMap.value.get(emojiSegment)

      if (!emojiObject && (emojiSegment === PLAIN_HEART_EMOJI_STRING || emojiSegment === RED_HEART_EMOJI_STRING)) {
        emojiObject = emojiUnicodeMap.value.get(RED_HEART_EMOJI_STRING)
      }

      if (!emojiObject) return []
      emojiObjects.push(emojiObject)
    }

    return emojiObjects.length > 0 && emojiObjects.length <= 3 ? emojiObjects : []
  }

  return {
    // State
    isLoading,
    emojiData,

    // Computed
    emojiDataAsFlatArray,
    emojiUnicodeMap,
    emojiCategorizedUrlMap,
    emojiCodeMap,

    // Methods
    fetchEmojiData,
    isEmojiCharacter,
    getEmojisData,
  }
})

export { useEmojisStore }
